import { makeStyles } from 'tss-react/mui';
import React, { SetStateAction, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  KeyValue,
  MUIDataTable,
  MUIDataTableColumnDef,
  Pill,
  PillVariant,
  TABLE_INITIAL_STATE,
  Typography,
  useDataTable,
} from 'web-library';

import FormHeader from '../../../components/form-items/FormHeader';
import {
  UniqueLink,
  useGetWithdrawalUniqueLinkListing,
} from '../../../api-refactored/unique-link/useGetUniqueLinkListing';
import UniqueLinkDetailsModal from './UniqueLinkDetailsModal';
import { GenericExamLevel } from '../../../api/candidate/exam/model';

const getPillStatus = (status: string): PillVariant => {
  switch (status) {
    case 'Valid':
    case 'Used':
      return 'positive';
    default:
      return 'negative';
  }
};

const useStyles = makeStyles()((theme) => ({
  dialog: {
    overflow: 'hidden',
  },
  buttonContainer: {
    marginTop: theme.spacing(3),
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  viewDetailsButton: {
    padding: 0,
    minHeight: 0,
    minWidth: 0,
    whiteSpace: 'nowrap',
    height: theme.spacing(3),
  },
}));

const tableColumns = (
  data: UniqueLink[],
  t: TFunction<'translation'>,
  classes: Record<string, string>,
  setUniqueLinkToken: React.Dispatch<SetStateAction<string | undefined>>,
  setDisableInvalidate: React.Dispatch<SetStateAction<boolean>>
): MUIDataTableColumnDef[] => [
  {
    name: 'token',
    label: t('admin->>UniqueLink.Token'),
    options: {
      sort: false,
      customBodyRenderLite: (dataIndex: number) => (
        <>
          <Typography variant="body2">{data[dataIndex].token}</Typography>
        </>
      ),
    },
  },
  {
    name: 'expiredDate',
    label: t('admin->>UniqueLink.ExpiryDate'),
    options: {
      sort: false,
      customBodyRenderLite: (dataIndex: number) => (
        <Typography variant="body2">{data[dataIndex].expiredDate}</Typography>
      ),
    },
  },
  {
    name: 'description',
    label: t('admin->>UniqueLink.Description'),
    options: {
      sort: false,
      customBodyRenderLite: (dataIndex: number) => (
        <Typography variant="body2">{data[dataIndex].description}</Typography>
      ),
    },
  },
  {
    name: 'status',
    label: t('admin->>UniqueLink.Status'),
    options: {
      sort: false,
      customBodyRenderLite: (dataIndex: number) => (
        <Pill variant={getPillStatus(data[dataIndex].status)} label={data[dataIndex].status} />
      ),
    },
  },
  {
    name: 'action',
    label: t('admin->>UniqueLink.Action'),
    options: {
      sort: false,
      customBodyRenderLite: (dataIndex: number) => (
        <Button
          className={classes.viewDetailsButton}
          color="primary"
          variant="text"
          size="small"
          onClick={() => {
            setUniqueLinkToken(data[dataIndex].token);
            setDisableInvalidate(data[dataIndex].status !== 'Valid');
          }}
        >
          {t('admin->>UniqueLink.ViewDetails')}
        </Button>
      ),
    },
  },
];

interface UniqueLinkListingModalProps {
  selectedCoordinatorUuid: string | undefined;
  setSelectedCoordinatorUuid: React.Dispatch<React.SetStateAction<string | undefined>>;
  examLevel: GenericExamLevel | undefined;
  userType: string | undefined;
}

const UniqueLinkListingModal = ({
  selectedCoordinatorUuid,
  setSelectedCoordinatorUuid,
  examLevel,
  userType,
}: UniqueLinkListingModalProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [selectedUniqueLinkToken, setSelectedUniqueLinkToken] = useState<string | undefined>(
    undefined
  );
  const [disableInvalidate, setDisableInvalidate] = useState(false);

  const {
    state,
    dispatch: tableDispatch,
    tOptions,
  } = useDataTable({
    ...TABLE_INITIAL_STATE,
  });

  const { data, refetch: refetchUniqueLinkListing } = useGetWithdrawalUniqueLinkListing({
    coordinatorUuid: selectedCoordinatorUuid,
    examLevel,
    onSuccess: (res) => {
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: res.model,
          count: res.model.length,
        },
      });
    },
  });

  const handleCloseUniqueLinkListingModal = () => {
    tableDispatch({ type: 'CLEAR_SELECTION' });
    setSelectedCoordinatorUuid(undefined);
  };

  const handleRefetchUniqueLinkListing = () => {
    refetchUniqueLinkListing().then();
  };

  return (
    <>
      <Dialog
        id="unique-link-listing-modal-dialog"
        fullWidth
        open={!!selectedCoordinatorUuid}
        maxWidth="lg"
        PaperProps={{ className: classes.dialog }}
      >
        <DialogTitle id="unique-link-listing-modal-title">
          {t('admin->>UniqueLink.ManageUniqueLink')}
        </DialogTitle>
        <DialogContent id="unique-link-listing-modal-content">
          <Box>
            <KeyValue
              keyText={
                userType === 'Candidate'
                  ? t('admin->>UniqueLink.CandidateName')
                  : t('admin->>UniqueLink.CoordinatorName')
              }
              valueText={data?.coordinatorName}
            />
            <FormHeader title={t('admin->>UniqueLink.UniqueLink')} />
            <MUIDataTable
              state={state}
              dispatch={tableDispatch}
              columns={tableColumns(
                state.data,
                t,
                classes,
                setSelectedUniqueLinkToken,
                setDisableInvalidate
              )}
              data={state.data}
              emptyDataLabel={t('admin->>UniqueLink.NoUniqueLink')}
              options={{
                ...tOptions,
                pagination: false,
                sort: false,
                selectToolbarPlacement: 'none',
                selectableRows: 'none',
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Box className={classes.buttonContainer}>
            <Button
              id="button-close-unique-link-listing-modal"
              type="button"
              variant="text"
              onClick={handleCloseUniqueLinkListingModal}
            >
              {t('admin->>UniqueLink.Close')}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <UniqueLinkDetailsModal
        uniqueLinkToken={selectedUniqueLinkToken}
        setSelectedUniqueLinkToken={setSelectedUniqueLinkToken}
        refetchUniqueLinkListing={handleRefetchUniqueLinkListing}
        disableInvalidate={disableInvalidate}
      />
    </>
  );
};
export default UniqueLinkListingModal;
